import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Send, Sparkles, RefreshCcw, Copy, Download, RotateCw } from "lucide-react";
import { aiChat, aiMonthlySummary, aiCashFlow, monthLabel } from "@/lib/finance";
import { formatInr } from "@/lib/format";
import { errorMessage } from "@/lib/api";

const PRESET_CHIPS = [
    "What was our net profit last month?",
    "Which expense category has grown the most?",
    "How is Kerala pilot cash flow trending?",
    "Compare this month vs last month",
    "Explain the biggest expense category",
];

export default function AiAccountantPage() {
    const [params, setParams] = useSearchParams();
    const [tab, setTab] = useState("chat");
    const [tokensUsed, setTokensUsed] = useState(0);

    return (
        <div data-testid="ai-accountant-page">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
                <div>
                    <div className="font-heading text-sm font-semibold text-[var(--brand-orange)]">Finance · AI</div>
                    <h1 className="mt-1 font-heading text-3xl font-extrabold text-[var(--text-primary)]">AI Accountant</h1>
                </div>
                <div className="text-xs text-[var(--text-muted)]" data-testid="tokens-used">Tokens used this session: {tokensUsed}</div>
            </div>
            <div className="mb-4 flex gap-2 lg:hidden">
                <Button variant={tab === "chat" ? "default" : "outline"} size="sm" onClick={() => setTab("chat")} data-testid="tab-chat">Chat</Button>
                <Button variant={tab === "reports" ? "default" : "outline"} size="sm" onClick={() => setTab("reports")} data-testid="tab-reports">Reports</Button>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
                <div className={tab === "chat" ? "block" : "hidden lg:block"}><ChatPanel onTokens={(t) => setTokensUsed((n) => n + t)} initialMessage={params.get("message")} /></div>
                <div className={tab === "reports" ? "block" : "hidden lg:block"}><ReportsPanel params={params} onTokens={(t) => setTokensUsed((n) => n + t)} /></div>
            </div>
        </div>
    );
}

function ChatPanel({ onTokens, initialMessage }) {
    const [messages, setMessages] = useState([]);
    const [convoId, setConvoId] = useState(null);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [fallback, setFallback] = useState(null);
    const scrollRef = useRef(null);
    // Phase 13c — React 18 StrictMode double-invokes effects in dev, which
    // caused the initial message to be sent (and rendered) twice. Guard the
    // one-shot init with a ref so it only fires exactly once per mount.
    const initSentRef = useRef(false);

    useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9 }); }, [messages, busy]);

    const send = async (text) => {
        const msg = (text || input).trim();
        if (!msg || busy) return;
        setMessages((m) => [...m, { role: "user", content: msg, ts: new Date() }]);
        setInput(""); setBusy(true); setFallback(null);
        try {
            const res = await aiChat(msg, convoId);
            setConvoId(res.conversation_id);
            if (res.reply) {
                setMessages((m) => [...m, { role: "assistant", content: res.reply, ts: new Date() }]);
                if (res.tokens_used) onTokens(res.tokens_used);
            } else if (res.error) {
                setFallback({ error: res.error, ctx: res.used_context });
            }
        } catch (e) {
            const status = e?.response?.status;
            if (status === 429) toast.error("Rate limit hit (30/min). Try again in a moment.");
            else if (status === 503) toast.error("AI unavailable. Try again later.");
            else toast.error(errorMessage(e));
        } finally { setBusy(false); }
    };

    useEffect(() => {
        if (!initialMessage) return;
        if (initSentRef.current) return; // StrictMode-safe idempotency guard
        initSentRef.current = true;
        send(initialMessage);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    return (
        <div className="card-soft flex h-[70vh] flex-col !p-0" data-testid="chat-panel">
            <div className="flex items-center justify-between border-b border-[var(--border-soft)] p-3">
                <div className="font-heading text-sm font-bold text-[var(--text-primary)]">Conversation</div>
                <Button size="sm" variant="ghost" onClick={() => { setMessages([]); setConvoId(null); setFallback(null); initSentRef.current = true; toast.info("New conversation"); }} data-testid="chat-clear"><RotateCw size={12} className="mr-1" /> New</Button>
            </div>
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3" data-testid="chat-messages">
                {messages.length === 0 && <div className="text-center text-xs text-[var(--text-muted)]">Ask me anything about your finances.</div>}
                {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                            className={
                                m.role === "user"
                                    ? "max-w-[85%] rounded-2xl bg-[#FFF3E6] px-3 py-2 font-body text-sm text-[var(--text-primary)]"
                                    : "max-w-[85%] rounded-2xl border border-[var(--border-soft)] bg-white px-3 py-2 font-body text-sm text-[var(--text-primary)] shadow-sm"
                            }
                            data-testid={`chat-bubble-${m.role}`}
                        >
                            {m.role === "assistant" ? <div className="prose prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div> : m.content}
                        </div>
                    </div>
                ))}
                {busy && <div className="text-xs text-[var(--text-muted)]" data-testid="chat-typing">AI Accountant is typing…</div>}
                {fallback && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="chat-fallback">
                        <b>AI unavailable — showing raw numbers.</b>
                        <pre className="mt-1 whitespace-pre-wrap">{JSON.stringify(fallback.ctx, null, 2)}</pre>
                    </div>
                )}
            </div>
            <div className="border-t border-[var(--border-soft)] p-3">
                <div className="mb-2 flex flex-wrap gap-1">
                    {PRESET_CHIPS.map((c) => (
                        <button key={c} onClick={() => send(c)} disabled={busy}
                            className="rounded-full border border-slate-300 bg-white px-2 py-0.5 font-heading text-[10px] font-semibold text-[var(--text-muted)] transition hover:border-[var(--brand-orange)] hover:text-[var(--brand-orange)] disabled:opacity-50"
                            data-testid={`chip-${c.slice(0, 10).replace(/\s/g, "-").toLowerCase()}`}>{c}</button>
                    ))}
                </div>
                <div className="flex gap-2">
                    <Input placeholder="Ask a question…" value={input} onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && send()} data-testid="chat-input" disabled={busy}
                        className="rounded-xl border-slate-200 focus:border-[var(--brand-orange)] focus-visible:ring-1 focus-visible:ring-[var(--brand-orange)]" />
                    <Button onClick={() => send()} disabled={busy || !input.trim()} className="btn-primary" data-testid="chat-send"><Send size={14} /></Button>
                </div>
            </div>
        </div>
    );
}

function ReportsPanel({ params, onTokens }) {
    const now = new Date();
    const [year, setYear] = useState(Number(params.get("year")) || now.getFullYear());
    const [month, setMonth] = useState(Number(params.get("month")) || now.getMonth() + 1);
    const [monthly, setMonthly] = useState(null);
    const [monthlyBusy, setMonthlyBusy] = useState(false);
    const [forecastMonths, setForecastMonths] = useState(6);
    const [forecast, setForecast] = useState(null);
    const [forecastBusy, setForecastBusy] = useState(false);

    const runMonthly = async (force = false) => {
        try {
            setMonthlyBusy(true);
            const r = await aiMonthlySummary(year, month, force);
            setMonthly(r);
            if (r.tokens_used) onTokens(r.tokens_used);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setMonthlyBusy(false); }
    };
    const runForecast = async (force = false) => {
        try {
            setForecastBusy(true);
            const r = await aiCashFlow(forecastMonths, force);
            setForecast(r);
            if (r.tokens_used) onTokens(r.tokens_used);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setForecastBusy(false); }
    };

    useEffect(() => { if (params.get("action") === "monthly-summary") runMonthly(false); /* eslint-disable-next-line */ }, []);

    const copyMd = (md) => { navigator.clipboard.writeText(md || ""); toast.success("Copied"); };
    const downloadMd = (md, name) => {
        const url = URL.createObjectURL(new Blob([md || ""], { type: "text/markdown" }));
        const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
    };
    const forecastSeries = useMemo(() => (forecast?.forecast_series || []).map((f) => ({
        name: monthLabel(f.year, f.month), Revenue: f.projected_revenue_paise / 100, Expenses: f.projected_expenses_paise / 100, Net: f.projected_net_paise / 100, confidence: f.confidence,
    })), [forecast]);

    return (
        <div className="space-y-4" data-testid="reports-panel">
            <Card className="rounded-2xl border border-neutral-200 p-4">
                <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-bold">Monthly Summary</div>
                    {monthly?.cached && <Badge className="bg-blue-100 text-blue-800" data-testid="monthly-cached">Cached</Badge>}
                </div>
                <div className="mb-2 flex flex-wrap gap-2">
                    <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                        <SelectTrigger className="w-28" data-testid="monthly-month"><SelectValue /></SelectTrigger>
                        <SelectContent>{Array.from({ length: 12 }, (_, i) => <SelectItem key={i + 1} value={String(i + 1)}>{new Date(2000, i, 1).toLocaleDateString("en", { month: "long" })}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                        <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                        <SelectContent>{[year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button onClick={() => runMonthly(false)} disabled={monthlyBusy} className="nest-gradient text-white" data-testid="monthly-run"><Sparkles size={12} className="mr-1" /> Generate</Button>
                    {monthly?.cached && <Button variant="outline" size="sm" onClick={() => runMonthly(true)} data-testid="monthly-refresh"><RefreshCcw size={12} className="mr-1" /> Refresh</Button>}
                </div>
                <div className="max-h-72 overflow-y-auto rounded-lg bg-neutral-50 p-3 text-sm" data-testid="monthly-markdown">
                    {monthlyBusy ? <Skeleton className="h-40" /> : monthly?.summary_markdown ? (
                        <div className="prose prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{monthly.summary_markdown}</ReactMarkdown></div>
                    ) : <div className="text-xs text-neutral-500">Click Generate to build the monthly report.</div>}
                </div>
                {monthly?.summary_markdown && (
                    <div className="mt-2 flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => copyMd(monthly.summary_markdown)} data-testid="monthly-copy"><Copy size={12} className="mr-1" /> Copy</Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadMd(monthly.summary_markdown, `report-${year}-${month}.md`)} data-testid="monthly-download"><Download size={12} className="mr-1" /> Download</Button>
                    </div>
                )}
            </Card>

            <Card className="rounded-2xl border border-neutral-200 p-4">
                <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-bold">Cash Flow Forecast</div>
                    {forecast?.cached && <Badge className="bg-blue-100 text-blue-800" data-testid="forecast-cached">Cached</Badge>}
                </div>
                <div className="mb-2 flex flex-wrap gap-2">
                    <Select value={String(forecastMonths)} onValueChange={(v) => setForecastMonths(Number(v))}>
                        <SelectTrigger className="w-24" data-testid="forecast-months"><SelectValue /></SelectTrigger>
                        <SelectContent>{[3, 6, 12].map((n) => <SelectItem key={n} value={String(n)}>{n} months</SelectItem>)}</SelectContent>
                    </Select>
                    <Button onClick={() => runForecast(false)} disabled={forecastBusy} className="nest-gradient text-white" data-testid="forecast-run"><Sparkles size={12} className="mr-1" /> Generate Forecast</Button>
                    {forecast?.cached && <Button variant="outline" size="sm" onClick={() => runForecast(true)} data-testid="forecast-refresh"><RefreshCcw size={12} className="mr-1" /> Refresh</Button>}
                </div>
                {forecastBusy ? <Skeleton className="h-64" /> : forecast?.forecast_series && (
                    <>
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={forecastSeries}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                    <XAxis dataKey="name" fontSize={11} />
                                    <YAxis fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                                    <Tooltip formatter={(v) => `₹${Math.round(v).toLocaleString("en-IN")}`} />
                                    <Legend />
                                    <Line type="monotone" dataKey="Revenue" stroke="#f97316" strokeWidth={2} />
                                    <Line type="monotone" dataKey="Expenses" stroke="#e11d48" strokeWidth={2} />
                                    <Line type="monotone" dataKey="Net" stroke="#059669" strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1 text-[10px]" data-testid="forecast-confidence-pills">
                            {forecast.forecast_series.map((f) => (
                                <Badge key={`${f.year}-${f.month}`} variant="outline">{monthLabel(f.year, f.month)}: {f.confidence}</Badge>
                            ))}
                        </div>
                        {forecast.narrative_markdown && (
                            <div className="mt-3 max-h-48 overflow-y-auto rounded-lg bg-neutral-50 p-3 text-sm" data-testid="forecast-narrative">
                                <div className="prose prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{forecast.narrative_markdown}</ReactMarkdown></div>
                            </div>
                        )}
                    </>
                )}
            </Card>
        </div>
    );
}
